import axios from "axios";
import { useEffect, useState } from "react";
import { IoCheckmarkCircleOutline } from "react-icons/io5";
import { SecureConnection } from "../../components/bankFeeds/secureConnection";
import { getBackendUrl, isDev, STORES } from "../../utils";
import {
  ATTEMPTED_LINKING_DATA_KEY,
  BankFeedAccountLinkingData,
  CONFIRMED_LINKING_DATA_KEY,
  INVALID_STATE_ERROR,
} from "./constants";
import { GenericBankFeedsButton } from "./GenericBankFeedsButton";
import { getMessageFromError, trackAmplitudeEvent } from "./utils/shared";

export const GenericBankFeedsLinkStepThreeComponent = ({
  token,
  store,
  restart,
  setStep,
  setComplete,
  platformName,
  setErrorMessage,
  setRetryCallback,
  transactionStartDate,
  setRetryableErrorMessage,
  bankFeedAccountLinkingData,
  overrideExisting,
}: {
  token: string;
  store: STORES | null;
  restart: string;
  setStep: (value: number) => void;
  setComplete: (value: boolean) => void;
  platformName: string | null;
  setErrorMessage: (value: string) => void;
  setRetryCallback: (fn: () => void) => void;
  transactionStartDate: string | null;
  setRetryableErrorMessage: (value: string) => void;
  bankFeedAccountLinkingData: BankFeedAccountLinkingData[];
  overrideExisting: boolean;
}) => {
  const [isLoading, setIsLoading] = useState(true);

  const saveLinkedState = () => {
    localStorage.setItem(
      CONFIRMED_LINKING_DATA_KEY,
      JSON.stringify(bankFeedAccountLinkingData),
    );
  };

  const updateErrorMessage = (args: { error?: any; message?: string }) => {
    const { error, message } = args;
    saveLinkedState();

    if (message) {
      trackAmplitudeEvent("Encountered an error", { errorMessage: message });
      // All errors from this screen should be retryable.
      setRetryableErrorMessage(message);
    } else if (!error) {
      trackAmplitudeEvent("Encountered an unknown error");
      setRetryableErrorMessage("Unknown error occurred. Please try again!");
    } else {
      const responseData = error.response?.data;
      const errorMessage = responseData?.error_message ?? error.message;
      trackAmplitudeEvent("Encountered an error", { errorMessage });
      setRetryableErrorMessage(errorMessage);
    }
  };

  const restartFlowWithToast = () => {
    // Restart the flow and display error toast. ONLY display the accounts from this list.
    localStorage.setItem(
      ATTEMPTED_LINKING_DATA_KEY,
      JSON.stringify(bankFeedAccountLinkingData),
    );

    setStep(1);
  };

  const updateBankFeedAccount = async (
    linkingData: BankFeedAccountLinkingData,
  ) => {
    await axios
      .patch(
        `${getBackendUrl()}/versioned/accounting/bank_feeds/accounts/${
          linkingData.bfAccountId
        }`,
        {
          bank_feed_account: {
            transaction_start_date:
              store === STORES.XERO ? undefined : transactionStartDate,
            account_id: linkingData.glAccountId,
            additional_fields: {
              override_existing: isDev() || overrideExisting,
            },
          },
        },
        {
          headers: {
            "X-Rutter-Lta": token,
            "X-Rutter-Version": "2024-08-31",
            "X-Rutter-Force-Sync": isDev(),
          },
        },
      )
      .catch((err) => {
        // Catch the error to track it, but then throw it so we know how many failed.
        trackAmplitudeEvent("Error updating bank feed account", {
          errorMessage: getMessageFromError(err),
          linkingData,
          transactionStartDate,
          store,
        });
        throw err;
      });
  };

  useEffect(() => {
    setRetryCallback(() => {
      return () => {
        window.location.reload();
      };
    });
  }, []);

  const validateLinkingData = (): string | null => {
    trackAmplitudeEvent("Validating linking data", {
      accountLinkingData: bankFeedAccountLinkingData,
    });

    if (bankFeedAccountLinkingData.length === 0) {
      return "No bank feed account links were found. Please exit the flow or try again.";
    }

    const linkedBFAccountIds = new Set<string>();
    const linkedGLAccountIds = new Set<string>();

    for (const data of bankFeedAccountLinkingData) {
      if (!data.isChecked) {
        trackAmplitudeEvent(
          "Validation error - Found unchecked account linking data",
          {
            accountLinkingDatum: data,
          },
        );
        return INVALID_STATE_ERROR.message;
      }

      if (
        linkedBFAccountIds.has(data.bfAccountId) ||
        linkedGLAccountIds.has(data.glAccountId)
      ) {
        trackAmplitudeEvent(
          "Validation error - Found accounts already linked ",
          {
            accountLinkingDatum: data,
            linkedBFAccountIds,
            linkedGLAccountIds,
          },
        );
        return INVALID_STATE_ERROR.message;
      }

      // If account types do not match (which theoretically should not be possible), we will error
      // on the API call and go back to the first step for relinking.

      linkedBFAccountIds.add(data.bfAccountId);
      linkedGLAccountIds.add(data.glAccountId);
    }

    return null;
  };

  useEffect(() => {
    if (!store) {
      updateErrorMessage(INVALID_STATE_ERROR);
      return;
    }

    // Xero bank feed connections do not support transaction start date, so there is no reason to collect it.
    if (store !== STORES.XERO && !transactionStartDate) {
      updateErrorMessage({
        message: "Please try entering the transaction start date again.",
      });
      return;
    }

    const validationError = validateLinkingData();
    if (validationError) {
      updateErrorMessage({ message: validationError });
      return;
    }

    trackAmplitudeEvent("Updating all confirmed bank feed accounts", {
      accountLinkingData: bankFeedAccountLinkingData,
    });

    const updateAllBankFeedAccounts = async () => {
      const promises = bankFeedAccountLinkingData.map(async (linkingData) => {
        await updateBankFeedAccount(linkingData);
      });

      const results = await Promise.allSettled(promises);
      if (results.some((result) => result.status === "rejected")) {
        restartFlowWithToast();
        return;
      }

      trackAmplitudeEvent("Bank feed account updates were successful!", {
        accountLinkingData: bankFeedAccountLinkingData,
      });
      setIsLoading(false);
      setComplete(true);
    };

    updateAllBankFeedAccounts();
  }, [setIsLoading, setComplete]);

  const confirmHandler = () => {
    trackAmplitudeEvent("Account mapping flow is complete");

    if (isDev() && restart === "true") {
      // Start over from the beginning for testing.
      setStep(1);
    } else {
      window.close();
    }
  };

  return isLoading ? (
    <SecureConnection />
  ) : (
    <>
      <div className="pt-4 pr-12 pl-12">
        <div className="flex justify-center items-center gap-3 mb-[16px]">
          <IoCheckmarkCircleOutline size={64} className="text-green-500" />
        </div>
        <div className="text-lg font-bold pb-2" style={{ textAlign: "center" }}>
          You're all set!
        </div>
        <div className="text-sm" style={{ textAlign: "center" }}>
          Your {platformName} accounts are now connected. Transactions will
          begin syncing shortly.
        </div>
      </div>

      {/* Ensure space b/w message and button. */}
      <div
        style={{
          minHeight: 275,
        }}></div>

      <GenericBankFeedsButton
        text="Done"
        onClick={confirmHandler}
        disabled={false}
      />
    </>
  );
};
